import { useMutation, useQuery } from '@tanstack/react-query';
import { platform } from '@platform/contracts';
import { useApi, useSession } from '@platform/app-kit';
import { PRODUCT } from '../shell/brand.ts';

/** Query keys carry the tenant id so nothing cached for one person is shown to another. */
export const keys = {
  plans: ['platform', 'signup', 'catalog', PRODUCT] as const,
  documents: ['platform', 'signup', 'documents', PRODUCT] as const,
  signupStatus: (slug: string) => ['platform', 'signup', 'status', slug] as const,
  billing: (tenantId: string | null) => ['billing', tenantId, 'summary'] as const,
};

/** A personal tenant's id doubles as its sign-up slug (`p-…`); anything else is not polled. */
export function isSlug(value: string | null | undefined): value is string {
  return typeof value === 'string' && platform.Slug.safeParse(value).success;
}

/** The plans this product sells (`GET /signup/catalog?product=`): one flat monthly package today. */
export function usePlans() {
  const api = useApi();
  return useQuery({
    queryKey: keys.plans,
    queryFn: async () =>
      (await api.public.platform.Signup_catalog({ query: { product: PRODUCT } })).items,
    staleTime: 5 * 60_000,
  });
}

/**
 * The documents a person accepts to sign up (`GET /signup/documents?product=`), each at its
 * current version with where to read it. Empty: the product asks for none, and nothing is shown.
 */
export function useSignupDocuments() {
  const api = useApi();
  return useQuery({
    queryKey: keys.documents,
    queryFn: async () =>
      (await api.public.platform.Signup_documents({ query: { product: PRODUCT } })).items,
    staleTime: 5 * 60_000,
  });
}

export type StartPlan = platform.PersonalSignupRequest;

/**
 * `POST /signup/personal { packageId, accepted?, noticeChannel? }` with the person's ID token:
 * reserves their tenant (or resumes it — a repeat never creates a second one) and starts the
 * pay-first onboarding. `accepted` names the versions the person was shown.
 */
export function useStartPlan() {
  const api = useApi();
  return useMutation({
    mutationFn: async (body: StartPlan) => (await api.platform.Signup_personal({ body })).data,
  });
}

/** Statuses after provisioning: the tenant exists and the person is its owner. */
export const READY_STATUSES: readonly platform.TenantStatus[] = ['active', 'suspended', 'locked'];

/**
 * `GET /signup/status?slug=` — public, polled every 3 s until the onboarding settles (ready or
 * failed). Before the payment page exists the status has no `checkoutUrl`; billing adds it.
 */
export function useSignupStatus(slug: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: keys.signupStatus(slug ?? ''),
    queryFn: async () =>
      (await api.public.platform.Signup_status({ query: { slug: slug ?? '' } })).data,
    enabled: isSlug(slug),
    retry: (count, error) => count < 3 && !isNotFound(error),
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      if (query.state.error && isNotFound(query.state.error)) return false;
      return s && (READY_STATUSES.includes(s) || s === 'failed') ? false : 3_000;
    },
  });
}

function isNotFound(error: unknown): boolean {
  return (error as { status?: unknown } | null)?.status === 404;
}

/** The plan's billing state (`GET /api/billing/summary`), for the owner (TenantAdmin). */
export function useBilling() {
  const api = useApi();
  const { principal } = useSession();
  const tenantId = principal?.tenantId ?? null;
  return useQuery({
    queryKey: keys.billing(tenantId),
    queryFn: async () => (await api.billing.Summary_get()).data,
    enabled: Boolean(tenantId),
  });
}

/** The billing portal (card, invoices, cancel): `POST /api/billing/portal`. */
export function useBillingPortal() {
  const api = useApi();
  return useMutation({
    mutationFn: async () =>
      (await api.billing.Portals_create({ body: { returnPath: '/' } })).data.portalUrl,
  });
}
